import { refreshTokenApi } from "@/api/services/admin/auth";
import { getCustomSentryExceptionError, handleApiErrorWithCode } from "@/utils/error";
import { removeToken, verifyToken } from "@/utils/token";
import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}/admin`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

export const clientApi = axios.create({
  baseURL,
});

// If successful (do nothing)
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

let isRefreshing = false;
let refreshQueue: ((config: AxiosRequestConfig) => void)[] = [];
//  in case of error
export const onError = async (error: AxiosError) => {
  if (error.response) {
    // Access Token was expired
    if (error.response.status === 401 && error.config) {
      Cookies.remove("admin-token");
      if (!isRefreshing) {
        const errorHeader = !error.config.headers.Authorization && Cookies.get("admin-token");

        if (!errorHeader) {
          try {
            const email = Cookies.get("email");
            const refreshToken = Cookies.get("admin-refresh-token");
            if (!email || !refreshToken) {
              logout();
              return;
            }
            isRefreshing = true;
            // Call an API to refresh the token here and store the new token
            await refreshTokenApi({ email, refreshToken });
            isRefreshing = false;
          } catch (error) {
            logout();
          }
        }

        const newToken = Cookies.get("admin-token");

        // Retry all the requests in the queue
        refreshQueue.forEach((request) =>
          request({
            headers: {
              Authorization: `Bearer ${newToken}`,
            },
          }),
        );
        refreshQueue = [];
        return axios({
          ...error.config,
          headers: {
            Authorization: `Bearer ${newToken}`,
          },
        });
      }

      // Return a promise to wait for the request to be retried after the token is updated
      return new Promise((resolve) => {
        refreshQueue.push((config: AxiosRequestConfig) => {
          resolve(axios(config));
        });
      });
    }
  }
  handleApiErrorWithCode(error);

  if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();

  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.response.use(onSuccess, onError);
globalApi.interceptors.response.use(onSuccess, onError);

export const setAxiosAuth = () => {
  const token = Cookies.get("admin-token");
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const isAuth = () => {
  if (typeof window !== "undefined") {
    const token = Cookies.get("admin-token");
    const isAuthenticated = !!token && verifyToken(token);
    return isAuthenticated;
  }
  return false;
};

export const logout = () => {
  removeToken();
  Cookies.remove("admin-user-id");
  Cookies.remove("admin-email");
};
